import {
  CONVERSATION_KEY_BYTES,
  ENVELOPE_ALG,
  ENVELOPE_VERSION,
  NONCE_BYTES,
  serialiseAad,
  type ContentKind,
  type ConversationPayload,
  type EncryptedEnvelope,
  type EnvelopeAad,
  type ParticipantRole,
} from '@kairelo/shared';

/**
 * Browser-side conversation cryptography (concept §5, §6).
 *
 * What this module is:
 *   - AES-256-GCM through Web Crypto, which is authenticated encryption;
 *   - keys from `crypto.getRandomValues`, which is a CSPRNG;
 *   - a fresh 96-bit nonce per encryption, never reused with a given key;
 *   - additional authenticated data binding every ciphertext to its
 *     conversation, its sender and its content kind.
 *
 * What this module is NOT: a new encryption algorithm. The artifact is explicit
 * that no custom cryptography is being proposed, and none is implemented here.
 *
 * What it deliberately cannot do: reach the server. The key never leaves the
 * browser. It is carried to the visitor in the URL fragment, which browsers do
 * not send in an HTTP request, and it is held locally on the owner's side.
 *
 * This is a V1 build baseline pending independent security review (SECURITY.md).
 */

/** Keys are non-extractable once imported for use, except where export is needed to persist them. */
const KEY_ALGORITHM = { name: 'AES-GCM', length: 256 } as const;

export class DecryptionError extends Error {
  constructor(message = 'This content could not be decrypted with the key held here.') {
    super(message);
    this.name = 'DecryptionError';
  }
}

/** True when the platform gives us the primitives the design requires. */
export function isCryptoAvailable(): boolean {
  return (
    typeof crypto !== 'undefined' &&
    typeof crypto.subtle !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  );
}

/* ------------------------------------------------------------------ encoding */

export function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64Url(value: string): Uint8Array {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/* --------------------------------------------------------------------- keys */

/**
 * Generates a conversation key in the owner's browser (concept §5).
 *
 * Exportable, because the key must be written into the URL fragment for the
 * visitor and persisted locally for the owner. Both are properties of the
 * approved design, not accidents.
 */
export async function generateConversationKey(): Promise<CryptoKey> {
  if (!isCryptoAvailable()) {
    throw new Error('This browser does not provide the Web Crypto API.');
  }
  return crypto.subtle.generateKey(KEY_ALGORITHM, true, ['encrypt', 'decrypt']);
}

export async function exportKey(key: CryptoKey): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.exportKey('raw', key));
}

export async function importKey(raw: Uint8Array): Promise<CryptoKey> {
  if (raw.byteLength !== CONVERSATION_KEY_BYTES) {
    throw new Error(`A conversation key must be ${CONVERSATION_KEY_BYTES} bytes.`);
  }
  return crypto.subtle.importKey('raw', raw as BufferSource, KEY_ALGORITHM, true, [
    'encrypt',
    'decrypt',
  ]);
}

export async function keyToFragmentValue(key: CryptoKey): Promise<string> {
  return toBase64Url(await exportKey(key));
}

export async function keyFromFragmentValue(value: string): Promise<CryptoKey> {
  return importKey(fromBase64Url(value));
}

/* ---------------------------------------------------------------- fragment */

/** The fragment parameter that carries the conversation key. */
const FRAGMENT_KEY_PARAM = 'k';

/**
 * Reads the conversation key out of the current URL fragment.
 *
 * The fragment is read locally and never transmitted. Callers should invoke
 * {@link clearFragment} immediately afterwards so the key stops being visible
 * in the address bar and in anything that later reads the location.
 */
export function readKeyFromFragment(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (raw.length === 0) return null;
  const params = new URLSearchParams(raw);
  const value = params.get(FRAGMENT_KEY_PARAM);
  return value && value.length > 0 ? value : null;
}

/** Builds the shareable invitation URL. Assembled in the browser only. */
export function buildInvitationUrl(origin: string, path: string, keyValue: string): string {
  return `${origin}${path}#${FRAGMENT_KEY_PARAM}=${keyValue}`;
}

/**
 * Removes the fragment from the address bar without navigating.
 *
 * Reduces the chance of the key being captured by a screenshot, shoulder
 * surfing, or anything that later inspects the location (concept §5).
 */
export function clearFragment(): void {
  if (typeof history === 'undefined' || typeof location === 'undefined') return;
  history.replaceState(null, '', `${location.pathname}${location.search}`);
}

/* ------------------------------------------------------------- encryption */

async function encryptBytes(
  key: CryptoKey,
  plaintext: Uint8Array,
  aad: EnvelopeAad,
): Promise<EncryptedEnvelope> {
  // A fresh nonce for every single encryption. GCM nonce reuse under one key is
  // catastrophic, so this is never derived, cached or counted.
  const iv = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
  const additionalData = new TextEncoder().encode(serialiseAad(aad));

  const ciphertext = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv as BufferSource,
      additionalData: additionalData as BufferSource,
      tagLength: 128,
    },
    key,
    plaintext as BufferSource,
  );

  return {
    v: ENVELOPE_VERSION,
    alg: ENVELOPE_ALG,
    iv: toBase64Url(iv),
    ct: toBase64Url(new Uint8Array(ciphertext)),
  };
}

async function decryptBytes(
  key: CryptoKey,
  envelope: EncryptedEnvelope,
  aad: EnvelopeAad,
): Promise<Uint8Array> {
  if (envelope.v !== ENVELOPE_VERSION || envelope.alg !== ENVELOPE_ALG) {
    throw new DecryptionError('This content uses an unsupported encryption format.');
  }
  const additionalData = new TextEncoder().encode(serialiseAad(aad));
  try {
    const plaintext = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: fromBase64Url(envelope.iv) as BufferSource,
        additionalData: additionalData as BufferSource,
        tagLength: 128,
      },
      key,
      fromBase64Url(envelope.ct) as BufferSource,
    );
    return new Uint8Array(plaintext);
  } catch {
    // A GCM failure means the key is wrong, the AAD does not match, or the
    // ciphertext was tampered with. The caller cannot tell which, and should
    // not: to the user it is simply unreadable here.
    throw new DecryptionError();
  }
}

/** Encrypts a structured conversation payload: a message, or the visitor name. */
export async function encryptPayload(params: {
  key: CryptoKey;
  conversationId: string;
  sender: ParticipantRole;
  payload: ConversationPayload;
}): Promise<EncryptedEnvelope> {
  const aad: EnvelopeAad = {
    v: ENVELOPE_VERSION,
    cid: params.conversationId,
    sender: params.sender,
    kind: params.payload.kind as ContentKind,
  };
  const plaintext = new TextEncoder().encode(JSON.stringify(params.payload));
  return encryptBytes(params.key, plaintext, aad);
}

export async function decryptPayload(params: {
  key: CryptoKey;
  conversationId: string;
  sender: ParticipantRole;
  kind: ContentKind;
  envelope: EncryptedEnvelope;
}): Promise<ConversationPayload> {
  const aad: EnvelopeAad = {
    v: ENVELOPE_VERSION,
    cid: params.conversationId,
    sender: params.sender,
    kind: params.kind,
  };
  const plaintext = await decryptBytes(params.key, params.envelope, aad);
  return JSON.parse(new TextDecoder().decode(plaintext)) as ConversationPayload;
}

/**
 * Encrypts an attachment's bytes for object storage.
 *
 * The result is uploaded straight to MinIO as `application/octet-stream`. The
 * filename and real media type stay inside the message envelope, so storage
 * metadata reveals neither (concept §6).
 */
export async function encryptBlob(params: {
  key: CryptoKey;
  conversationId: string;
  sender: ParticipantRole;
  kind: ContentKind;
  data: ArrayBuffer;
}): Promise<{ ciphertext: Uint8Array; iv: string }> {
  const aad: EnvelopeAad = {
    v: ENVELOPE_VERSION,
    cid: params.conversationId,
    sender: params.sender,
    kind: params.kind,
  };
  const envelope = await encryptBytes(params.key, new Uint8Array(params.data), aad);
  return { ciphertext: fromBase64Url(envelope.ct), iv: envelope.iv };
}

export async function decryptBlob(params: {
  key: CryptoKey;
  conversationId: string;
  sender: ParticipantRole;
  kind: ContentKind;
  ciphertext: ArrayBuffer;
  iv: string;
}): Promise<Uint8Array> {
  const aad: EnvelopeAad = {
    v: ENVELOPE_VERSION,
    cid: params.conversationId,
    sender: params.sender,
    kind: params.kind,
  };
  return decryptBytes(
    params.key,
    {
      v: ENVELOPE_VERSION,
      alg: ENVELOPE_ALG,
      iv: params.iv,
      ct: toBase64Url(new Uint8Array(params.ciphertext)),
    },
    aad,
  );
}
